<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useAuthStore } from '@/stores/auth';
import kycApi from '@/api/modules/kycApi';
import type { GlobalReportListItem } from '@/types/kyc';
import { formatDate, formatTaxId } from '@/utils/formatters';
import { getRiskLevelDisplay } from '@/utils/kyc';
import BaseIcon from '@/components/base/BaseIcon.vue';

const props = withDefaults(defineProps<{
    title?: string;
    limit?: number;
}>(), {
    title: 'Relatórios Recentes',
    limit: 5
});

const authStore = useAuthStore();
const recentReports = ref<GlobalReportListItem[]>([]);
const loading = ref(true);

const fetchRecentReports = async () => {
    loading.value = true;
    try {
        const response = await kycApi.getGlobalReports({
            page: 1,
            page_size: props.limit,
            user_id: authStore.user?.id,
        });
        recentReports.value = response.items;
    } catch (error) {
        console.error('Failed to fetch recent reports:', error);
    } finally {
        loading.value = false;
    }
};

onMounted(fetchRecentReports);

const getIcon = (type: string) => {
    switch (type) {
        case 'company': return 'domain';
        case 'person': return 'person';
        default: return 'description';
    }
};

const getRoute = (item: GlobalReportListItem) => {
    if (item.entity_type === 'company') {
        return { name: 'company-kyc-lookup', params: { cnpj: item.entity_tax_id } };
    } else if (item.entity_type === 'person') {
        if (item.entity_uuid) {
            return { name: 'person-kyc', params: { uuid: item.entity_uuid } };
        } else {
             return { name: 'person-lookup' }; 
        }
    }
    return { name: 'home' };
};

const formatTime = (dateStr: string) => {
    return formatDate(dateStr, { hour: '2-digit', minute: '2-digit' }, '');
};

const getRisk = (level: number | null | undefined) => getRiskLevelDisplay(level);
</script>

<template>
    <div class="recent-reports-card">
        <h3 v-if="title" class="title">
            <BaseIcon class="text-2xl text-primary-500 mr-2">description</BaseIcon>
            {{ title }}
        </h3>

        <div v-if="loading" class="loading-state">
            <span class="material-icons spin">refresh</span>
        </div>

        <div v-else-if="recentReports.length === 0" class="empty-state">
            <p>Nenhum relatório criado recentemente.</p>
        </div>

        <ul v-else class="activity-list">
            <li v-for="item in recentReports" :key="item.request_id" class="activity-item">
                <router-link :to="getRoute(item)" class="activity-link">
                    <!-- Entity Icon -->
                    <div class="icon-wrapper" :class="item.entity_type">
                        <BaseIcon class="text-2xl">{{ getIcon(item.entity_type) }}</BaseIcon>
                    </div>
                    
                    <div class="info flex-grow">
                        <div class="flex justify-between items-start mb-1">
                            <div class="flex flex-col">
                                <span class="label text-surface-900 font-semibold">{{ item.entity_name || item.entity_tax_id }}</span>
                                <span class="sub-text font-mono text-xs text-surface-500">{{ formatTaxId(item.entity_tax_id) }}</span>
                            </div>
                            <span class="timestamp text-xs text-surface-400">{{ formatTime(item.inserted_at) }}</span>
                        </div>
                        
                        <div class="flex justify-between items-center mt-2">
                            <div class="flex flex-col gap-1 text-xs text-surface-600">
                                <span>
                                  Políticas: {{ item.risk_profile_name }}
                                  <template v-if="item.risk_profile_deleted_at"> (Excluída)</template>
                                </span>
                                <span v-if="item.created_by_user_name">Por: {{ item.created_by_user_name }}</span>
                            </div>

                            <div class="flex items-center gap-2">
                                <!-- Status Icon for Failed -->
                                <div v-if="item.status === 'failed'" class="status-icon failed" title="Relatório falhou">
                                    <BaseIcon class="text-red-500">error</BaseIcon>
                                </div>
                                
                                <!-- Risk Level Badge -->
                                <div 
                                    v-if="item.status === 'completed'" 
                                    :class="[
                                        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border',
                                        getRisk(item.overall_risk_level_value).bgClass,
                                        getRisk(item.overall_risk_level_value).colorClass,
                                        getRisk(item.overall_risk_level_value).borderClass
                                    ]"
                                >
                                    <BaseIcon class="text-sm">{{ getRisk(item.overall_risk_level_value).icon }}</BaseIcon>
                                    <span>{{ getRisk(item.overall_risk_level_value).label }}</span>
                                </div>
                                <div v-else-if="item.status !== 'failed'" class="text-xs text-surface-500 italic">
                                    {{ item.status === 'processing' ? 'Processando...' : 'Pendente' }}
                                </div>
                            </div>
                        </div>
                    </div>
                </router-link>
            </li>
        </ul>
    </div>
</template>

<style scoped>
.recent-reports-card {
    background: #fff;
    border-radius: 12px;
    padding: 1.25rem;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    border: 1px solid #f1f3f5;
    height: 100%;
}

.title {
    font-size: 1.1rem;
    font-weight: 700;
    color: #1a1d23;
    margin-bottom: 1.25rem;
    display: flex;
    align-items: center;
}

.activity-list {
    list-style: none;
    padding: 0;
    margin: 0;
}

.activity-item {
    margin-bottom: 0.5rem;
}

.activity-link {
    display: flex;
    align-items: center;
    text-decoration: none;
    color: inherit;
    padding: 0.75rem;
    border-radius: 10px;
    transition: all 0.2s ease;
    border: 1px solid transparent;
}

.activity-link:hover {
    background-color: #f8f9fa;
    border-color: #e9ecef;
    transform: translateY(-1px);
}

.icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 10px;
    margin-right: 1rem;
    flex-shrink: 0;
}

.icon-wrapper.company { background-color: #e7f1ff; color: #0d6efd; }
.icon-wrapper.person { background-color: #e6ffed; color: #28a745; }

.info {
    min-width: 0;
}

.status-icon.failed {
    cursor: help;
}

.loading-state, .empty-state {
    text-align: center;
    padding: 2rem 1rem;
    color: #adb5bd;
}

.spin {
    animation: spin 1s linear infinite;
    font-size: 2rem;
}

@keyframes spin { 100% { transform: rotate(360deg); } }
</style>
